import React from 'react'
import styled from '@emotion/styled'
import { colors, font, radius, shadow, spacing } from '@mobi/component-library/Foundations'
import { debounce } from '@mobi/utils/functions'

export const Card = React.forwardRef<
  HTMLDivElement,
  {
    children: React.ReactNode
    topComponent?: React.ReactNode
    onClick?: React.MouseEventHandler
    padding?: 'sm' | 'none'
    className?: string
    testId?: string
  }
>(({ children, topComponent, onClick, testId = 'ContentCard', padding = 'sm', className }, ref) => {
  const localRef = React.useRef<HTMLDivElement | null>(null)

  const setCombinedRefs = (node: HTMLDivElement) => {
    localRef.current = node
    if (typeof ref === 'function') {
      ref(node)
    } else if (ref) {
      ref.current = node
    }
  }

  React.useEffect(() => {
    if (!localRef.current || !ResizeObserver) return
    const el = localRef.current

    const handleResize = debounce(() => handleActiveScaleAdjust(el), 100)
    const observer = new ResizeObserver(handleResize)
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <CardStyled
      ref={setCombinedRefs}
      data-testid={testId}
      data-has-click={!!onClick}
      data-padding={padding}
      className={className}
    >
      <div>{topComponent}</div>

      <div onClick={onClick}>{children}</div>
    </CardStyled>
  )
})

Card.displayName = 'Card'

// ============
// Local Helper
// ============

function handleActiveScaleAdjust(el: HTMLDivElement) {
  const height = el.offsetHeight
  if (height > 500 && height < 1000) {
    el.style.setProperty('--activeScale', '0.98')
    return
  }
  if (height >= 1000) {
    el.style.setProperty('--activeScale', '1')
  }
}

// ======
// Styles
// ======

const CardStyled = styled.div({
  '--activeScale': '0.95',
  '--borderRadius': radius.lgx3,

  overflow: 'clip',
  '@supports not (overflow: "clip")': { overflow: 'hidden' },

  border: '1px solid ' + colors.neutral[300],
  borderRadius: 'var(--borderRadius)',
  boxShadow: shadow.xs,
  fontFamily: font.family.primary,

  '> div:first-of-type': {
    overflow: 'clip',
    '@supports not (overflow: "clip")': { overflow: 'hidden' },
    borderTopLeftRadius: 'var(--borderRadius)',
    borderTopRightRadius: 'var(--borderRadius)',

    ':empty': { display: 'none' },
  },

  '> div:last-of-type': {
    display: 'flex',
    flexDirection: 'column',
    gap: spacing.smx1,
    padding: 0,
    backgroundColor: colors.white,
    borderBottomLeftRadius: 'var(--borderRadius)',
    borderBottomRightRadius: 'var(--borderRadius)',
  },

  '&[data-has-click="true"]': {
    userSelect: 'none',
    willChange: 'transform',
    transition: 'all 0.3s ease',
    transform: 'translate3d(0, 0, 0)',
    transformOrigin: 'center center',
    touchAction: 'manipulation',

    '@media (hover: hover) and (pointer: fine)': {
      cursor: 'pointer',

      '> div:last-of-type': { transition: 'all 0.3s ease' },
      '&:hover > div:last-of-type': { backgroundColor: colors.surface[50] },
    },

    '&:active:not(:has(button:active))': { transform: 'scale(var(--activeScale))' },
    '&:active:not(:has(button:active)) > *': { opacity: 0.75 },
  },

  '&[data-padding="sm"]': {
    '> div:last-of-type': {
      padding: spacing.sm,
    },
  },
})
